import {
  deleteToken,
  getToken,
  onMessage,
  type MessagePayload,
} from "firebase/messaging";
import {
  FIREBASE_VAPID_KEY,
  getRestaurantFirebaseMessaging,
} from "./config";
import { isRestaurantNotificationType } from "./notification-types";

export type PushTokenFailureReason =
  | "config"
  | "permission"
  | "network"
  | "timeout"
  | "unsupported";

export type PushTokenResult =
  | { ok: true; token: string }
  | { ok: false; reason: PushTokenFailureReason; error?: unknown };

const PUSH_OPERATION_TIMEOUT_MS = 10_000;
const PUSH_TIMEOUT = Symbol("restaurant-push-timeout");
const RESTAURANT_SERVICE_WORKER_URL = import.meta.env.DEV
  ? "/dev-sw.js?dev-sw"
  : "/restaurant-sw.js";

async function withTimeout<T>(promise: Promise<T>, timeoutMs: number): Promise<T> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    return await Promise.race([
      promise,
      new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(PUSH_TIMEOUT), timeoutMs);
      }),
    ]);
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

export type RestaurantPushTokenOperations = {
  registerServiceWorker: () => Promise<ServiceWorkerRegistration>;
  requestToken: (registration: ServiceWorkerRegistration) => Promise<string>;
  timeoutMs?: number;
};

/**
 * Bound both SDK operations independently. Firebase exposes no AbortSignal for
 * these calls, so timeout means callers may retry while lifecycle generation
 * guards prevent a late result from registering after logout/unmount.
 */
export async function acquireRestaurantPushToken(
  operations: RestaurantPushTokenOperations,
): Promise<PushTokenResult> {
  const timeoutMs = operations.timeoutMs ?? PUSH_OPERATION_TIMEOUT_MS;
  try {
    const registration = await withTimeout(
      operations.registerServiceWorker(),
      timeoutMs,
    );
    const token = await withTimeout(
      operations.requestToken(registration),
      timeoutMs,
    );
    return token
      ? { ok: true, token }
      : { ok: false, reason: "network" };
  } catch (error) {
    const reason = error === PUSH_TIMEOUT ? "timeout" : "network";
    console.warn("[restaurant-notifications] FCM token request failed", {
      reason,
      error,
    });
    return { ok: false, reason, error };
  }
}

export function supportsRestaurantPush(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof Notification !== "undefined" &&
    typeof navigator !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window
  );
}

async function registerRestaurantServiceWorker(): Promise<ServiceWorkerRegistration> {
  const registration = await navigator.serviceWorker.register(
    RESTAURANT_SERVICE_WORKER_URL,
    {
      scope: "/",
      ...(import.meta.env.DEV ? { type: "module" as const } : {}),
    },
  );
  return registration.active
    ? registration
    : await navigator.serviceWorker.ready;
}

export async function requestRestaurantPushToken(): Promise<PushTokenResult> {
  if (!supportsRestaurantPush()) return { ok: false, reason: "unsupported" };
  if (Notification.permission !== "granted") {
    return { ok: false, reason: "permission" };
  }
  if (!FIREBASE_VAPID_KEY) {
    return { ok: false, reason: "config" };
  }
  const messaging = getRestaurantFirebaseMessaging();
  if (!messaging) return { ok: false, reason: "config" };

  return acquireRestaurantPushToken({
    registerServiceWorker: registerRestaurantServiceWorker,
    requestToken: (registration) =>
      getToken(messaging, {
        vapidKey: FIREBASE_VAPID_KEY,
        serviceWorkerRegistration: registration,
      }),
  });
}

/**
 * Revoke this browser's notification delivery when the authenticated backend
 * DELETE cannot be completed. Keep the service worker when either Firebase's
 * registration deletion or the direct Push subscription removal succeeds, so
 * a later login can register normally; unregister it only as the last local
 * privacy boundary.
 */
export async function disableLocalRestaurantPushDelivery(): Promise<void> {
  const registration =
    typeof navigator !== "undefined" && "serviceWorker" in navigator
      ? await navigator.serviceWorker
          .getRegistration("/")
          .catch(() => undefined)
      : undefined;
  const messaging = getRestaurantFirebaseMessaging();
  const firebaseDeleted = messaging
    ? await deleteToken(messaging).catch(() => false)
    : false;
  const subscription = await registration?.pushManager
    .getSubscription()
    .catch(() => null);
  const unsubscribed = subscription
    ? await subscription.unsubscribe().catch(() => false)
    : true;
  if (!firebaseDeleted && !unsubscribed) {
    await registration?.unregister().catch(() => false);
  }
}

export function subscribeToRestaurantForegroundMessages(
  listener: (payload: MessagePayload) => void,
): () => void {
  const messaging = getRestaurantFirebaseMessaging();
  if (!messaging) return () => undefined;
  try {
    return onMessage(messaging, (payload) => {
      if (isRestaurantNotificationType(payload.data?.type)) listener(payload);
    });
  } catch (error) {
    console.warn(
      "[restaurant-notifications] Foreground messaging unavailable",
      error,
    );
    return () => undefined;
  }
}
